import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Row, Col, Button, Input, Modal, Tag, Empty, Spin, message, Select, Dropdown } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBookOpen, faPlus, faArrowLeft, faEllipsisV, faPlay, faEdit, faTrash, faCopy, faClock } from '@fortawesome/free-solid-svg-icons';
import { fetchPlaybooks, createPlaybook, updatePlaybook, deletePlaybook } from '../../../Slices/bulkOutreachSlice';
import { useNavigate } from 'react-router-dom';

const glassCard = {
  borderRadius: 16, border: '1px solid rgba(255,255,255,0.18)',
  background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.9) 100%)',
  boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
};

const PlaybookManager = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { playbooks, playbooksTotal, loading } = useSelector(state => state.bulkOutreach);
  const [search, setSearch] = useState('');
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', category: 'General', isPublic: false });

  useEffect(() => { dispatch(fetchPlaybooks({ search })); }, [dispatch, search]);

  const handleCreate = async () => {
    if (!form.name.trim()) return message.warning('Playbook name is required');
    const result = await dispatch(createPlaybook(form));
    if (!result.error) { message.success('Playbook created'); setCreateModal(false); setForm({ name: '', description: '', category: 'General', isPublic: false }); }
  };

  const handleDelete = async (id) => {
    Modal.confirm({
      title: 'Delete Playbook?', content: 'This cannot be undone.',
      okText: 'Delete', okButtonProps: { danger: true },
      onOk: async () => {
        await dispatch(deletePlaybook(id));
        message.success('Playbook deleted');
      },
    });
  };

  const handleUse = (playbook) => {
    navigate('/bulk-outreach/new', { state: { playbook } });
  };

  return (
    <div style={{ padding: '24px 32px', minHeight: '100vh', background: '#f8fafc' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Button type="text" onClick={() => navigate('/bulk-outreach')} icon={<FontAwesomeIcon icon={faArrowLeft} />} />
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>
            <FontAwesomeIcon icon={faBookOpen} style={{ marginRight: 10, color: '#8b5cf6' }} />
            Campaign Playbooks
          </h2>
        </div>
        <Button type="primary" icon={<FontAwesomeIcon icon={faPlus} style={{ marginRight: 6 }} />}
          onClick={() => setCreateModal(true)}
          style={{ background: '#8b5cf6', borderColor: '#8b5cf6', borderRadius: 8, height: 40 }}>
          New Playbook
        </Button>
      </div>

      <Input.Search placeholder="Search playbooks..." value={search} onChange={e => setSearch(e.target.value)}
        style={{ marginBottom: 24, maxWidth: 400, borderRadius: 8 }} allowClear />

      {loading && <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>}

      {!loading && playbooks.length === 0 && (
        <Empty description="No playbooks yet. Create one to save reusable campaign configs." style={{ marginTop: 60 }} />
      )}

      <Row gutter={[16, 16]}>
        {playbooks.map(pb => (
          <Col key={pb._id} xs={24} sm={12} lg={8} xl={6}>
            <Card style={{ ...glassCard, height: '100%', cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s' }}
              hoverable bodyStyle={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 600, color: '#1a1a2e' }}>{pb.name}</h3>
                  <Tag color="#8b5cf6" style={{ fontSize: 11 }}>{pb.category}</Tag>
                </div>
                <Dropdown menu={{
                  items: [
                    { key: 'use', label: 'Use in Campaign', icon: <FontAwesomeIcon icon={faPlay} /> },
                    { key: 'edit', label: 'Edit', icon: <FontAwesomeIcon icon={faEdit} /> },
                    { key: 'delete', label: 'Delete', icon: <FontAwesomeIcon icon={faTrash} />, danger: true },
                  ],
                  onClick: ({ key }) => {
                    if (key === 'use') handleUse(pb);
                    if (key === 'edit') setEditModal(pb);
                    if (key === 'delete') handleDelete(pb._id);
                  },
                }} trigger={['click']}>
                  <Button type="text" size="small" icon={<FontAwesomeIcon icon={faEllipsisV} />} />
                </Dropdown>
              </div>

              {pb.description && <p style={{ margin: '8px 0', fontSize: 13, color: '#6b7280', lineHeight: 1.4 }}>{pb.description}</p>}

              <div style={{ marginTop: 12, display: 'flex', gap: 12, fontSize: 12, color: '#9ca3af' }}>
                <span>{pb.steps?.length || 0} steps</span>
                <span>|</span>
                <span>Used {pb.useCount || 0}x</span>
              </div>

              {pb.subjectLine && (
                <div style={{ marginTop: 8, fontSize: 12, color: '#6b7280', fontStyle: 'italic' }}>
                  Subject: {pb.subjectLine.slice(0, 50)}{pb.subjectLine.length > 50 ? '...' : ''}
                </div>
              )}

              <div style={{ marginTop: 12, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {(pb.steps || []).slice(0, 3).map((s, i) => (
                  <Tag key={i} style={{ fontSize: 11, borderRadius: 4 }} color={['#3b82f6', '#f59e0b', '#10b981'][i]}>
                    Step {i + 1}: {s.templateName || s.targeting || 'Custom'}
                  </Tag>
                ))}
                {(pb.steps || []).length > 3 && <Tag style={{ fontSize: 11 }}>+{pb.steps.length - 3} more</Tag>}
              </div>

              <div style={{ marginTop: 16 }}>
                <Button type="primary" block onClick={() => handleUse(pb)}
                  style={{ background: '#10b981', borderColor: '#10b981', borderRadius: 8 }}>
                  <FontAwesomeIcon icon={faPlay} style={{ marginRight: 6 }} /> Use This Playbook
                </Button>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Create Modal */}
      <Modal open={createModal} onCancel={() => setCreateModal(false)} title="Create Playbook" okText="Create" onOk={handleCreate}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 16 }}>
          <Input placeholder="Playbook name *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
          <Input.TextArea placeholder="Description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} />
          <Select value={form.category} onChange={v => setForm({ ...form, category: v })}
            options={[{ value: 'General' }, { value: 'Cold Outreach' }, { value: 'Follow-up' }, { value: 'Re-engagement' }, { value: 'Onboarding' }]} />
        </div>
      </Modal>
    </div>
  );
};

export default PlaybookManager;
